import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterModule,
  RouterOutlet,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { AuthService } from '../../../api/services/auth.service';
import { IntegrationStateService } from '../../../features/admin/security-integration-hub/integration-marketplace/integration-state.service';
import { IntegrationIconService } from '../../../api/services/integration-icon.service';
import { Subscription, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from '../../../api/services/common.service';
import { environment } from '../../../../environments/environment';
import { ICONS } from '../../../helpers/constants/icons.constant';
import { FORM_ID } from '../../../helpers/form-id.enum';
import { getAccessValue, getRoleAccess } from '../../../helpers/rbac.helper';

type SidebarItemAction =
  | 'navigate'
  | 'myWorkTasks'
  | 'myWorkActionables'
  | 'allTasks'
  | 'configuration'
  | 'external';

interface SubMenuItemConfig {
  moduleId: string;
  childId: string;
  label?: string;
  icon: string;
  color: string;
  route?: string;
  action?: SidebarItemAction;
  stepId?: string;
  externalUrl?: string;
  isConnected?: boolean;
  status?: string;
}

interface SubMenuSectionConfig {
  label: string;
  items: SubMenuItemConfig[];
  isExpanded?: boolean;
  originalCategory?: string;
}

@Component({
  selector: 'app-sub-menu',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: './sub-menu.component.html',
  styleUrls: ['./sub-menu.component.css'],
})
export class SubMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollViewport') private scrollViewport!: ElementRef<HTMLElement>;

  private subMenuIconCache: Record<string, SafeHtml> = {};
  private readonly subMenuSanitizer: DomSanitizer;
  private readonly expandedSidebarWidth = '272px';
  private readonly collapsedSidebarWidth = '80px';
  private _onScroll?: () => void;
  private _ro?: ResizeObserver;
  private readonly cd: ChangeDetectorRef;

  profileResponse: any;
  activeStepId: string | null = null;
  canScrollDown = false;
  canScrollUp = false;

  hoveredItem: SubMenuItemConfig | null = null;
  tooltipTop = 0;
  tooltipLeft = 152;
  hideScrollbar = window.innerWidth <= 1400;
  isMouseOverSidebar = false;
  private scrollbarTimeout?: any;
  private integrationSub?: Subscription;
  private readonly cancelApi$ = new Subject<void>();
  isLoading = false;
  responseData: any;

  get isSmallerDesktop(): boolean {
    return window.innerWidth <= 1400;
  }

  dynamicIntegrationSections: SubMenuSectionConfig[] = [];
  integrationSearchQuery = '';
  totalIntegrationsCount = 0;

  get isSidebarCollapsed(): boolean {
    return document.body.classList.contains('sidebar-collapse');
  }

  readonly ecisoSections: SubMenuSectionConfig[] = [
    {
      label: 'Executive View',
      items: [
        {
          moduleId: 'security-command-center',
          childId: 'posture-executive-dashboard',
          label: 'Dashboard',
          icon: 'menu-grid',
          color: '#6676ff',
          route: '/eciso/executive-dashboard',
        },
      ],
    },
    {
      label: 'My Workspace',
      items: [
        {
          moduleId: 'my-workspace',
          childId: 'my-dashboard',
          label: 'My Dashboard',
          icon: 'menu-grid',
          color: '#6676ff',
          route: '/eciso/dashboard',
        },
        {
          moduleId: 'my-workspace',
          childId: 'my-tasks',
          label: 'My Tasks',
          icon: 'clipboard-list',
          color: '#00BFFF',
          route: '/eciso/my-tasks',
          action: 'myWorkTasks',
        },
        {
          moduleId: 'my-workspace',
          childId: 'my-action-items',
          label: 'My Action Items',
          icon: 'check-square',
          color: '#00F6FF',
          route: '/eciso/my-action-items',
          action: 'myWorkActionables',
        },
        {
          moduleId: 'my-workspace',
          childId: 'my-bookmarks',
          label: 'My Bookmarks',
          icon: 'bookmark',
          color: '#FFA7D1',
          route: '/eciso/bookmarks',
        },
      ],
    },
    {
      label: 'Control & Task Operations',
      items: [
        {
          moduleId: 'control-task-operations',
          childId: 'task-dashboard',
          label: 'Task Dashboard',
          icon: 'menu-grid',
          color: '#6676ff',
          route: '/eciso/task-dashboard',
        },
        {
          moduleId: 'control-task-operations',
          childId: 'all-tasks',
          label: 'All Tasks',
          icon: 'clipboard-list',
          color: '#00d9ff',
          route: '/eciso/tasks',
          action: 'allTasks',
        },
        // {
        //   moduleId: 'control-task-operations',
        //   childId: 'recurring-activities',
        //   label: 'Recurring Activities',
        //   icon: 'recurring',
        //   color: '#8ea1c7',
        //   route: '/eciso/activities',
        // },
        {
          moduleId: 'control-task-operations',
          childId: 'all-action-items',
          label: 'All Action Items',
          icon: 'check-square',
          color: '#00d9ff',
          route: '/eciso/all-action-items',
        },
        {
          moduleId: 'control-task-operations',
          childId: 'evidence-vault',
          label: 'Evidence Vault',
          icon: 'vault',
          color: '#16bdf0',
          route: '/eciso/evidence-vault',
        },

        {
          moduleId: 'control-task-operations',
          childId: 'assets-list',
          label: 'Assets',
          icon: 'asset-card',
          color: '#FFB841',
          route: '/eciso/assets-list',
        },
      ],
    },
    {
      label: 'Security Overview',
      items: [
        {
          moduleId: 'security-landscape',
          childId: 'security-landscape',
          label: 'Security Landscape',
          icon: 'landscape',
          color: '#6684FF',
          route: '/eciso/security-landscape',
        },
      ],
    },
    {
      label: 'Logs',
      items: [
        {
          moduleId: 'security-command-center',
          childId: 'scc-activity-logs',
          label: 'Activity Logs',
          icon: 'activity',
          color: '#E0AE06',
          route: '/eciso/activity-logs',
        },
      ],
    },
  ];

  readonly ecomplianceSections: SubMenuSectionConfig[] = [
    {
      label: 'Compliance & Governance',
      items: [
        {
          moduleId: 'compliance-governance',
          childId: 'compliance-dashboard',
          label: 'Dashboard',
          icon: 'menu-grid',
          color: '#6676ff',
          route: '/eciso/compliance-dashboard',
        },
      ],
    },
    {
      label: 'Library',
      items: [
        {
          moduleId: 'compliance-governance',
          childId: 'framework-library',
          label: 'Framework Library',
          icon: 'framework-list',
          color: '#960BAC',
          route: '/eciso/framework-library',
        },
        // {
        //   moduleId: 'compliance-governance',
        //   childId: 'policy-library',
        //   label: 'Policy Library',
        //   icon: 'policy-library',
        //   color: '#40CD8B',
        //   route: '/eciso/policy-management',
        // },
        {
          moduleId: 'compliance-governance',
          childId: 'evidence-library',
          label: 'Evidence Library',
          icon: 'evidence-library',
          color: '#9572FF',
          route: '/eciso/evidence-library',
        },
      ],
    },
  ];

  readonly eriskSections: SubMenuSectionConfig[] = [
    {
      label: 'Risk & Impact Management',
      items: [
        {
          moduleId: 'risk-impact-management',
          childId: 'risk-dashboard',
          label: 'Risk Dashboard',
          icon: 'menu-grid',
          color: '#6676ff',
          route: '/eciso/risk-dashboard',
        },
      ],
    },
    {
      label: 'Risk Register',
      items: [
        {
          moduleId: 'risk-impact-management',
          childId: 'system-risk',
          label: 'System Risk',
          icon: 'system-risk',
          color: '#E15356',
          route: '/eciso/system-risk',
        },
        {
          moduleId: 'risk-impact-management',
          childId: 'manual-risk',
          label: 'Manual Risk',
          icon: 'manual-risk',
          color: '#FF5C5C',
          route: '/eciso/manual-risk',
        },
      ],
    },
  ];

  readonly settingsSections: SubMenuSectionConfig[] = [
    {
      label: 'SETTINGS',
      items: [
        {
          moduleId: 'settings-module',
          childId: 'organization',
          label: 'Organization',
          icon: 'organization',
          color: '#0F766E',
          route: '/eciso/organization-setting',
        },
        {
          moduleId: 'settings-module',
          childId: 'employees',
          label: 'Employees',
          icon: 'employee',
          color: '#0F766E',
          route: '/eciso/employees',
        },
        {
          moduleId: 'settings-module',
          childId: 'access-management',
          label: 'Access Management',
          icon: 'access-management',
          color: '#4F46E5',
          route: '/eciso/access-management',
        },
        {
          moduleId: 'settings-module',
          childId: 'my-profile',
          label: 'My Profile',
          icon: 'profile-circle',
          color: '#0EA5E9',
          route: '/eciso/my-profile',
        },
      ],
    },
    {
      label: 'GENERAL',
      items: [
        {
          moduleId: 'settings-module',
          childId: 'general-settings',
          label: 'General Settings',
          icon: 'general-settings',
          color: '#00DCE4',
          route: '/eciso/general-settings',
        },
        {
          moduleId: 'settings-module',
          childId: 'email-settings',
          label: 'Email Settings',
          icon: 'notification',
          color: '#F59E0B',
          action: 'configuration',
          stepId: FORM_ID.NOTIFICATIONS_INTEGRATIONS,
        },
      ],
    },
    // {
    //   label: 'CONFIGURATIONS',
    //   items: [
    //     {
    //       moduleId: 'configurations',
    //       childId: 'application-control',
    //       label: 'Application Control',
    //       icon: 'application-control',
    //       color: '#B67DFD',
    //       action: 'configuration',
    //       stepId: '68511d54c23e7a0f7ae563ff',
    //     },
    //     {
    //       moduleId: 'configurations',
    //       childId: 'data-strategy',
    //       label: 'Data Strategy',
    //       icon: 'data-strategy',
    //       color: '#75B162',
    //       action: 'configuration',
    //       stepId: '68511d73c23e7a0f7ae56405',
    //     },
    //     {
    //       moduleId: 'configurations',
    //       childId: 'infrastructure-management',
    //       label: 'Infrastructure Management',
    //       icon: 'infrastructure-management',
    //       color: '#82A1EE',
    //       action: 'configuration',
    //       stepId: '68511dafc23e7a0f7ae56412',
    //     },
    //     {
    //       moduleId: 'configurations',
    //       childId: 'ot-ics-dcs-scada',
    //       label: 'OT/ICS/DCS/SCADA',
    //       icon: 'ot-ics-dcs-scada',
    //       color: '#9F9F23',
    //       action: 'configuration',
    //       stepId: '68511dc8c23e7a0f7ae56427',
    //     },
    //     {
    //       moduleId: 'configurations',
    //       childId: 'training-management',
    //       label: 'Training Management',
    //       icon: 'training-management',
    //       color: '#0D689C',
    //       action: 'configuration',
    //       stepId: '68511e30c23e7a0f7ae5643d',
    //     },
    //   ],
    // },
    // {
    //   label: 'APPEARANCE',
    //   items: [
    //     {
    //       moduleId: 'settings-module',
    //       childId: 'theme',
    //       label: 'Theme',
    //       icon: 'theme',
    //       color: '#00DCE4',
    //       route: '/eciso/appearance',
    //     },
    //     {
    //       moduleId: 'settings-module',
    //       childId: 'company-branding',
    //       label: 'Company Branding',
    //       icon: 'branding',
    //       color: '#FF0184',
    //       route: '/eciso/branding',
    //     },
    //   ],
    // },
    {
      label: 'APPLICATIONS',
      items: [
        {
          moduleId: 'settings-module',
          childId: 'theme',
          label: 'Theme',
          icon: 'theme',
          color: '#00DCE4',
          route: '/eciso/appearance',
        },
        {
          moduleId: 'settings-module',
          childId: 'reorder-app',
          label: 'Reorder App',
          icon: 'reorder',
          color: '#6893FF',
          route: '/eciso/reorder',
        },
        {
          moduleId: 'settings-module',
          childId: 'personal-app',
          label: 'Personal App',
          icon: 'personal-app',
          color: '#FF6B9A',
          route: '/eciso/personal',
        },
      ],
    },
  ];

  readonly notificationSections: SubMenuSectionConfig[] = [
    {
      label: 'Overview',
      items: [
        {
          moduleId: 'settings-module',
          childId: 'notification',
          label: 'Inbox',
          icon: 'notification',
          color: '#FF5C5C',
          route: '/eciso/notifications',
        },
        {
          moduleId: 'settings-module',
          childId: 'notification-preferences',
          label: 'Notification Preferences',
          icon: 'notification-preferences',
          color: '#3B82F6',
          route: '/eciso/notification-preferences',
        },
      ],
    },
  ];

  readonly defaultIntegrationSections: SubMenuSectionConfig[] = [
    {
      label: 'INTEGRATION HUB',
      items: [
        {
          moduleId: 'integration-hub',
          childId: 'integrations',
          label: 'Integrations',
          icon: 'integration',
          color: '#FFDC14',
          route: '/eciso/integration-marketplace',
        },
      ],
    },
  ];

  get integrationSections(): SubMenuSectionConfig[] {
    if (
      this.integrationSearchQuery &&
      this.integrationSearchQuery.trim() !== ''
    ) {
      return this.dynamicIntegrationSections;
    }
    return this.dynamicIntegrationSections.length > 0
      ? this.dynamicIntegrationSections
      : this.defaultIntegrationSections;
  }

  get subMenuSections(): SubMenuSectionConfig[] {
    const url = this.router.url;
    if (
      url.includes('/compliance-dashboard') ||
      url.includes('/framework-library') ||
      url.includes('/framework-details') ||
      url.includes('/policy-management') ||
      url.includes('/evidence-library') ||
      url.startsWith('/ecompliance')
    ) {
      return this.ecomplianceSections;
    }
    if (
      url.includes('/risk-dashboard') ||
      url.includes('/system-risk') ||
      url.includes('/risk-register') ||
      url.includes('/manual-risk') ||
      url.includes('/risk-details') ||
      url.startsWith('/erisk')
    ) {
      return this.eriskSections;
    }
    if (
      url.includes('/notifications') ||
      url.includes('/notification-preferences')
    ) {
      return this.notificationSections;
    }
    if (
      url.includes('/onboarding') ||
      url.includes('/organization-setting') ||
      url.includes('/employee') ||
      url.includes('/access-management') ||
      url.includes('/my-profile') ||
      url.includes('/general-settings') ||
      url.includes('/appearance') ||
      url.includes('/branding') ||
      url.includes('/reorder') ||
      url.includes('/personal') ||
      url.includes('/settings')
    ) {
      return this.settingsSections;
    }
    if (url.includes('/integration')) {
      return this.integrationSections;
    }
    return this.ecisoSections;
  }

  get currentModuleLogo(): string | null {
    const sections = this.subMenuSections;
    if (sections === this.ecisoSections) return './assets/app-icons/eSCC.svg';
    if (sections === this.ecomplianceSections)
      return './assets/app-icons/eCOM.svg';
    if (sections === this.eriskSections) return './assets/app-icons/eRR.svg';
    return null;
  }

  get currentModuleName(): string {
    const sections = this.subMenuSections;
    if (sections === this.ecisoSections) return 'Security Command Center';
    if (sections === this.ecomplianceSections) return 'Compliance & Governence';
    if (sections === this.eriskSections) return 'Risk & Impact Management';
    if (sections === this.notificationSections) return 'Notification Center';
    if (sections === this.settingsSections) return 'System Settings';
    if (sections === this.integrationSections) return 'Integrations Hub';
    return 'Menu';
  }

  get currentModuleClass(): string {
    const sections = this.subMenuSections;
    if (sections === this.ecisoSections) return 'module-security';
    if (sections === this.ecomplianceSections) return 'module-compliance';
    if (sections === this.eriskSections) return 'module-risk';
    if (sections === this.notificationSections) return 'module-notifications';
    if (sections === this.settingsSections) return 'module-settings';
    if (sections === this.integrationSections) return 'module-integration';
    return 'module-default';
  }

  selectedCategory: string | null = null;
  selectedIntegration: string | null = null;
  private categorySub?: Subscription;
  private refreshSub?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formService: FormsServiceService,
    private authService: AuthService,
    sanitizer: DomSanitizer,
    cd: ChangeDetectorRef,
    private integrationStateService: IntegrationStateService,
    private commonService: CommonService,
    private integrationIconService: IntegrationIconService,
  ) {
    this.subMenuSanitizer = sanitizer;
    this.cd = cd;

    this.categorySub = this.integrationStateService.selectedCategory$.subscribe(
      (category) => {
        this.selectedCategory = category;
        this.cd.markForCheck();
      },
    );

    this.integrationSub =
      this.integrationStateService.selectedIntegration$.subscribe(
        (integration) => {
          this.selectedIntegration = integration;
          this.cd.markForCheck();
        },
      );

    this.refreshSub =
      this.integrationStateService.refreshIntegrations$.subscribe(() => {
        if (this.currentModuleName === 'Integrations Hub') {
          this.loadIntegrations();
        }
      });
  }

  ngAfterViewInit(): void {
    this.syncScrollState();
    // Re-sync after paint in case async content (profile API) changes heights
    requestAnimationFrame(() => this.syncScrollState());
    if (this.isSidebarCollapsed) {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 50);
    }
    this._onScroll = () => {
      this.syncScrollState();
      if (this.hoveredItem) {
        this.hoveredItem = null;
        this.cd.detectChanges();
      }
      this.showScrollbarBriefly();
    };
    this.scrollViewport.nativeElement.addEventListener(
      'scroll',
      this._onScroll,
    );
    this._ro = new ResizeObserver(() => this.syncScrollState());
    this._ro.observe(this.scrollViewport.nativeElement);
  }

  ngOnDestroy(): void {
    this._ro?.disconnect();
    if (this._onScroll)
      this.scrollViewport.nativeElement.removeEventListener(
        'scroll',
        this._onScroll,
      );
    if (this.scrollbarTimeout) {
      clearTimeout(this.scrollbarTimeout);
    }
    this.categorySub?.unsubscribe();
    this.integrationSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
    this.cancelApi$.next();
    this.cancelApi$.complete();
  }

  scrollDown(): void {
    const el = this.scrollViewport.nativeElement;
    el.scrollBy({ top: Math.ceil(el.clientHeight * 0.75), behavior: 'smooth' });
  }

  scrollUp(): void {
    const el = this.scrollViewport.nativeElement;
    el.scrollBy({
      top: -Math.ceil(el.clientHeight * 0.75),
      behavior: 'smooth',
    });
  }

  private syncScrollState(): void {
    const el = this.scrollViewport.nativeElement;
    const newDown = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    const newUp = el.scrollTop > 4;
    if (this.canScrollDown !== newDown || this.canScrollUp !== newUp) {
      this.canScrollDown = newDown;
      this.canScrollUp = newUp;
      this.cd.detectChanges();
    }
  }

  ngOnInit(): void {
    this.getProfileDetails();

    const isCollapsed =
      window.innerWidth >= 1024 &&
      localStorage.getItem('sidebar_collapsed') === 'true';

    if (isCollapsed) {
      document.body.classList.add('sidebar-collapse');
      this.setSidebarWidth(true);
    } else {
      document.body.classList.remove('sidebar-collapse');
      this.setSidebarWidth(false);
    }

    this.setActiveStepFromUrl();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setActiveStepFromUrl();
        this.checkCurrentRouteAccessAndRedirect();
        if (
          this.currentModuleName === 'Integrations Hub' &&
          this.dynamicIntegrationSections.length === 0
        ) {
          this.loadIntegrations();
        }
        this.cd.detectChanges();
      }
    });

    if (this.currentModuleName === 'Integrations Hub') {
      this.loadIntegrations();
    }
  }

  loadIntegrations() {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
    };
    const formId = '6919b404c2ed5bad29a60d47';
    const baseUrl = `${environment.api_endpoint}/api/v1/form/data/list/${formId}`;

    // First fetch all records (default limit) to identify totalCount
    this.formService
      .loadData('GET', `${baseUrl}?sort=-platformName`, headers)
      .subscribe({
        next: (allRes: any) => {
          const totalCount = allRes.data?.totalCount || 0;
          const allRecords = allRes.data?.records || [];

          const proceedWithCategories = (records: any[]) => {
            const uniqueCategories = Array.from(
              new Set(
                records
                  .map((r: any) => r.category || r.categoryName)
                  .filter(Boolean),
              ),
            ) as string[];

            const requests = uniqueCategories.map((cat) => {
              const url = `${baseUrl}?category=${encodeURIComponent(cat)}&limit=${totalCount}&sort=-platformName`;
              return this.formService.loadData('GET', url, headers);
            });

            import('rxjs').then(({ forkJoin }) => {
              forkJoin(requests).subscribe({
                next: (responses: any[]) => {
                  let processedTotalCount = 0;
                  this.dynamicIntegrationSections = responses
                    .map((res, index) => {
                      const catRecords = res.data?.records || [];
                      const validRecords = catRecords.filter(
                        (r: any) => r._id !== 'None',
                      );
                      processedTotalCount += validRecords.length;
                      const categoryName = uniqueCategories[index];

                      let displayLabel = this.formatCategoryLabel(categoryName);

                      const mappedItems = validRecords
                        .map((item: any) => ({
                          moduleId: 'integration-hub',
                          childId: item._id,
                          label: item.platformName,
                          icon:
                            item.iconUrl ||
                            item.icon ||
                            this.integrationIconService.getFallbackIconUrl(
                              item,
                            ) ||
                            'integration',
                          color: '#ffffff',
                          action: 'external',
                          isConnected: item.status === 'Connected',
                          status: item.status,
                          categoryName: categoryName,
                        }))
                        .sort((a: any, b: any) => {
                          const aConn = a.status === 'Connected' ? 1 : 0;
                          const bConn = b.status === 'Connected' ? 1 : 0;
                          if (bConn !== aConn) {
                            return bConn - aConn;
                          }
                          return (a.label || '').localeCompare(b.label || '');
                        });

                      return {
                        label: `${displayLabel} (${validRecords.length.toString().padStart(2, '0')})`,
                        originalCategory: categoryName,
                        isExpanded: false,
                        items: mappedItems,
                      };
                    })
                    .filter((section) => section.items.length > 0)
                    .sort((a, b) => {
                      const aHasConn = a.items.some(
                        (item: any) => item.status === 'Connected',
                      )
                        ? 1
                        : 0;
                      const bHasConn = b.items.some(
                        (item: any) => item.status === 'Connected',
                      )
                        ? 1
                        : 0;
                      return bHasConn - aHasConn;
                    });

                  this.totalIntegrationsCount = processedTotalCount;

                  if (this.dynamicIntegrationSections.length > 0) {
                    let targetCat = this.selectedCategory;
                    let targetSection = this.dynamicIntegrationSections.find(
                      (sec: any) => {
                        if (!sec.originalCategory || !targetCat) return false;
                        const sCat = sec.originalCategory.toLowerCase().trim();
                        const tCat = targetCat.toLowerCase().trim();
                        if (sCat === tCat) return true;
                        return (
                          (sCat === 'iam' ||
                            sCat === 'identity management' ||
                            sCat === 'identity & access management') &&
                          (tCat === 'iam' ||
                            tCat === 'identity management' ||
                            tCat === 'identity & access management')
                        );
                      },
                    );

                    if (!targetSection) {
                      targetSection = this.dynamicIntegrationSections[0];
                      targetCat = (targetSection as any).originalCategory;
                    }

                    this.dynamicIntegrationSections.forEach((sec: any) => {
                      sec.isExpanded = sec === targetSection;
                    });

                    if (targetCat) {
                      this.integrationStateService.setSelectedCategory(
                        targetCat,
                      );
                    }
                  }

                  this.cd.detectChanges();
                },
                error: (err) =>
                  console.error('Error fetching integration categories', err),
              });
            });
          };

          if (totalCount > allRecords.length) {
            this.formService
              .loadData(
                'GET',
                `${baseUrl}?limit=${totalCount}&sort=-platformName`,
                headers,
              )
              .subscribe({
                next: (fullRes: any) => {
                  proceedWithCategories(fullRes.data?.records || []);
                },
                error: (err) => console.error(err),
              });
          } else {
            proceedWithCategories(allRecords);
          }
        },
        error: (err) =>
          console.error(
            'Error fetching all integrations for category parsing',
            err,
          ),
      });
  }

  setActiveStepFromUrl(): void {
    const tree = this.router.parseUrl(this.router.url);
    const segments = tree.root.children['primary']?.segments;

    if (!segments) return;

    const onboardingIndex = segments.findIndex((s) => s.path === 'onboarding');

    if (onboardingIndex > -1 && segments[onboardingIndex + 1]) {
      this.activeStepId = segments[onboardingIndex + 1].path;
    }
  }

  getProfileDetails() {
    let token = localStorage.getItem('accessToken');
    this.authService.getProfile(token).subscribe({
      next: (data) => {
        this.profileResponse = data.data;
        this.checkCurrentRouteAccessAndRedirect();
        this.cd.detectChanges();
      },
      error: (err) => console.error('Error', err),
    });
  }

  checkCurrentRouteAccessAndRedirect(): void {
    if (!this.profileResponse) return;

    const sections = this.subMenuSections;
    const allItems: SubMenuItemConfig[] = [];
    for (const section of sections) {
      allItems.push(...section.items);
    }

    const currentItem = allItems.find((item) => this.isSidebarItemActive(item));

    if (currentItem && !this.canShowSidebarItem(currentItem)) {
      const allowedItem = allItems.find((item) =>
        this.canShowSidebarItem(item),
      );
      if (allowedItem) {
        this.navigateToItem(allowedItem);
      }
    }
  }

  navigateToItem(item: SubMenuItemConfig): void {
    switch (item.action) {
      case 'myWorkTasks':
        this.goToMyWorkTasks();
        break;
      case 'myWorkActionables':
        this.onMyWorkActionablesClick();
        break;
      case 'allTasks':
        this.onTasksMenuClick();
        break;
      case 'configuration':
        if (item.stepId) {
          this.loadForm(item.stepId);
        }
        break;
      default:
        if (item.route) {
          this.navigateTo(item.route);
        }
        break;
    }
  }

  hasAccess(moduleId: string, childId?: string): boolean {
    const exemptChildIds = [
      'theme',
      'company-branding',
      'reorder-app',
      'personal-app',
      'notification',
      'notification-preferences',
      'general-settings',
      'email-settings',
    ];
    if (childId && exemptChildIds.includes(childId)) {
      return true;
    }
    if (!this.profileResponse) return false;
    const accessValue = getAccessValue(this.profileResponse, moduleId, childId);
    return !accessValue || accessValue !== 'no_access';
  }

  canShowSidebarItem(item: SubMenuItemConfig): boolean {
    return this.hasAccess(item.moduleId, item.childId);
  }

  hasVisibleSubMenuItems(section: SubMenuSectionConfig): boolean {
    return section.items.some((item) => this.canShowSidebarItem(item));
  }

  trackBySubMenuSection(_: number, section: SubMenuSectionConfig): string {
    return section.label;
  }

  trackBySubMenuItem(_: number, item: SubMenuItemConfig): string {
    return `${item.moduleId}.${item.childId}`;
  }

  getSubMenuIcon(icon: string): SafeHtml {
    if (this.subMenuIconCache[icon]) {
      return this.subMenuIconCache[icon];
    }

    this.subMenuIconCache[icon] = this.subMenuSanitizer.bypassSecurityTrustHtml(
      ICONS[icon] ?? ICONS['menu-grid'],
    );
    return this.subMenuIconCache[icon];
  }

  private categoryIconCache: Record<string, SafeHtml> = {};

  getCategoryIcon(categoryName: string): SafeHtml {
    const rawName = (categoryName || '').trim();
    if (this.categoryIconCache[rawName]) {
      return this.categoryIconCache[rawName];
    }

    const svg = this.resolveCategorySvg(rawName);
    this.categoryIconCache[rawName] =
      this.subMenuSanitizer.bypassSecurityTrustHtml(svg);
    return this.categoryIconCache[rawName];
  }

  isCategoryActive(section: SubMenuSectionConfig): boolean {
    if (!this.selectedCategory || !section.originalCategory) {
      return false;
    }
    const sCat = section.originalCategory.toLowerCase().trim();
    const tCat = this.selectedCategory.toLowerCase().trim();
    if (sCat === tCat) return true;
    return (
      (sCat === 'iam' ||
        sCat === 'identity management' ||
        sCat === 'identity & access management') &&
      (tCat === 'iam' ||
        tCat === 'identity management' ||
        tCat === 'identity & access management')
    );
  }

  formatCategoryLabel(cat: string): string {
    if (!cat) return '';
    const trimmed = cat.trim();
    const lower = trimmed.toLowerCase();
    if (lower === 'iam') return 'IAM';
    if (lower === 'siem / xdr' || lower === 'siem/xdr' || lower === 'siem')
      return 'SIEM / XDR';
    if (
      lower === 'itsm & operations' ||
      lower === 'itsm/operations' ||
      lower === 'itsm'
    )
      return 'ITSM & Operations';
    if (lower === 'fim') return 'File Integrity Monitoring';

    if (trimmed !== trimmed.toUpperCase() && trimmed !== trimmed.toLowerCase()) {
      return trimmed;
    }

    return trimmed
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private resolveCategorySvg(categoryName: string): string {
    const n = (categoryName || '').toLowerCase().trim();

    // 1. Endpoint Security (Shield with star/plus)
    if (n.includes('endpoint')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 7.5l1.2 3.3 3.3 1.2-3.3 1.2-1.2 3.3-1.2-3.3-3.3-1.2 3.3-1.2z" fill="currentColor"/></svg>`;
    }

    // 2. Asset Management (Stacked cylinders)
    if (n.includes('asset')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="8" ry="2.5"/><path d="M4 5v4.5c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5"/><path d="M4 9.5v4.5c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V9.5"/><path d="M4 14v4.5c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V14"/></svg>`;
    }

    // 3. Network Security (Connected cross nodes)
    if (n.includes('network')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="6" cy="18" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M8 8l8 8M16 8l-8 8"/></svg>`;
    }

    // 4. Data Security (Document with padlock)
    if (n.includes('data')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><rect x="9" y="13" width="6" height="5" rx="1"/><path d="M10.5 13v-1.5a1.5 1.5 0 0 1 3 0V13"/></svg>`;
    }

    // 5. Remote Access (Padlock)
    if (n.includes('remote')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/><circle cx="12" cy="16" r="1.5" fill="currentColor"/></svg>`;
    }

    // 6. Zero Trust Architecture (Circle target compass)
    if (n.includes('zero trust')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polygon points="12 6.5 15.5 12 12 17.5 8.5 12" stroke-width="1.8"/><circle cx="12" cy="12" r="1.5" fill="currentColor"/></svg>`;
    }

    // 7. Cloud Security (Cloud)
    if (n.includes('cloud')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z"/></svg>`;
    }

    // 8. ITSM & Operations (Briefcase with tag)
    if (n.includes('itsm') || n.includes('operation')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="7" width="13" height="13" rx="2"/><path d="M6 7V5a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2"/><path d="M16 11h4a1.5 1.5 0 0 1 1.5 1.5v4a1.5 1.5 0 0 1-1.5 1.5h-4"/><circle cx="18.5" cy="14.5" r="1" fill="currentColor"/></svg>`;
    }

    // 9. Application Security (Shield with pin / app symbol)
    if (n.includes('application') || n.includes('app sec')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 9v5"/><circle cx="12" cy="8" r="1" fill="currentColor"/><path d="M9.5 12l2.5 3 2.5-3"/></svg>`;
    }

    // 10. SIEM / XDR (Rounded display with pulse waveform)
    if (n.includes('siem') || n.includes('xdr') || n.includes('soc')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="16" rx="3"/><path d="M4 13h4l2.5-4.5 3 8 2.5-3.5h4"/></svg>`;
    }

    // 11. Key & Identity Management (Shield with vertical double arrow)
    if (
      n.includes('key') ||
      n.includes('identity') ||
      n.includes('iam') ||
      n.includes('access management')
    ) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v8"/><path d="M9.5 10.5L12 8l2.5 2.5"/><path d="M9.5 13.5L12 16l2.5-2.5"/></svg>`;
    }

    // 12. File Integrity Monitoring (Padlock / FIM)
    if (n.includes('file integrity') || n.includes('fim')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/><circle cx="12" cy="16" r="1.5" fill="currentColor"/></svg>`;
    }

    // 13. Vulnerability Management
    if (n.includes('vulnerability')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>`;
    }

    // 14. Email Security / Email Configuration
    if (n.includes('email')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><polyline points="3 7 12 13 21 7"/></svg>`;
    }

    // 15. Deception Technology
    if (n.includes('deception')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><circle cx="12" cy="11" r="3"/><path d="M12 2v3"/></svg>`;
    }

    // 16. Threat Intelligence
    if (n.includes('threat')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3"/></svg>`;
    }

    // 17. Patch Management
    if (n.includes('patch')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="8" rx="4" transform="rotate(-45 12 12)"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>`;
    }

    // 18. Risk Management
    if (n.includes('risk')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4M12 16h.01"/></svg>`;
    }

    // 19. Secure Service Edge
    if (n.includes('edge') || n.includes('sse')) {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><circle cx="12" cy="12" r="4"/><path d="M2 12h6M16 12h6"/></svg>`;
    }

    // Default Security Shield
    return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`;
  }

  getIconUrl(config: SubMenuItemConfig): string {
    const optionForFallback = {
      platformName: config?.label,
      name: config?.label,
      iconUrl: config?.icon,
    };
    if (this.integrationIconService.hasLocalMapping(optionForFallback)) {
      const localFallback =
        this.integrationIconService.getFallbackIconUrl(optionForFallback);
      if (localFallback) {
        return localFallback;
      }
    }
    const url = config?.icon;
    if (url) {
      const lowerUrl = url.toLowerCase();
      if (
        lowerUrl.includes('solarwindsfileintegritymonitor.png') ||
        lowerUrl.includes('solarwindspatchmanager.png') ||
        lowerUrl.includes('solarwindsnetflowtrafficanalyzer.png') ||
        lowerUrl.includes('solarwindsnetworkperformancemonitor.png')
      ) {
        return '/assets/media/integration/solarwinds.png';
      }
      return url;
    }
    const localFallback =
      this.integrationIconService.getFallbackIconUrl(optionForFallback);
    if (localFallback) {
      return localFallback;
    }
    return '/assets/media/integration/default.svg';
  }

  isImageUrl(icon: string): boolean {
    return (
      !!icon &&
      (icon.startsWith('http') ||
        icon.startsWith('assets/') ||
        icon.startsWith('/assets/'))
    );
  }

  handleSidebarImageError(config: SubMenuItemConfig, event: Event) {
    const image = event.target as HTMLImageElement;
    if (!image) {
      return;
    }

    const currentStep = image.dataset['fallbackStep'] || 'original';

    if (currentStep === 'original') {
      image.dataset['fallbackStep'] = 'local';
      const optionForFallback = {
        platformName: config.label,
        name: config.label,
        iconUrl: config.icon,
      };
      const localFallback =
        this.integrationIconService.getFallbackIconUrl(optionForFallback);
      if (
        localFallback &&
        localFallback !== '/assets/media/integration/default.svg' &&
        !image.src.endsWith(localFallback)
      ) {
        image.classList.remove('sub-menu-default-icon');
        image.src = localFallback;
        return;
      }
    }

    const domain = this.getDomainForSidebar(config);

    if (
      (image.dataset['fallbackStep'] === 'local' ||
        currentStep === 'original') &&
      domain
    ) {
      image.dataset['fallbackStep'] = 'google';
      image.classList.remove('sub-menu-default-icon');
      image.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=128&default=404`;

      image.onload = () => {
        if (image.dataset['fallbackStep'] === 'google') {
          if (image.naturalWidth < 16 && image.naturalHeight < 16) {
            this.trySidebarDuckDuckGoOrFallback(image, domain);
          }
        }
      };

      image.onerror = () => {
        if (image.dataset['fallbackStep'] === 'google') {
          this.trySidebarDuckDuckGoOrFallback(image, domain);
        }
      };
      return;
    }

    this.setSidebarDefaultIcon(image);
  }

  trySidebarDuckDuckGoOrFallback(image: HTMLImageElement, domain: string) {
    image.dataset['fallbackStep'] = 'duckduckgo';
    image.classList.remove('sub-menu-default-icon');
    image.onload = () => {
      if (image.naturalWidth < 16 && image.naturalHeight < 16) {
        this.setSidebarDefaultIcon(image);
      }
    };
    image.onerror = () => {
      this.setSidebarDefaultIcon(image);
    };
    image.src = `https://icons.duckduckgo.com/ip3/${domain}.ico`;
  }

  setSidebarDefaultIcon(image: HTMLImageElement) {
    image.onload = null;
    image.onerror = null;
    image.dataset['fallbackStep'] = 'default';
    image.classList.add('sub-menu-default-icon');
    image.src = '/assets/media/integration/default.svg';
  }

  getDomainForSidebar(config: SubMenuItemConfig): string {
    const url = config?.icon;
    if (
      url &&
      /^https?:\/\//i.test(url) &&
      !url.includes('blob.core.windows.net') &&
      !url.includes('localhost')
    ) {
      try {
        return new URL(url).hostname.replace(/^www\./, '');
      } catch (e) {}
    }
    const name = config?.label || config?.childId || '';
    if (!name) return '';
    let cleanName = name.toLowerCase().trim();

    if (cleanName.includes('godaddy')) {
      return 'godaddy.com';
    }
    if (cleanName.includes('cloudflare')) {
      return 'cloudflare.com';
    }
    if (cleanName.includes('juniper')) {
      return 'juniper.net';
    }
    if (cleanName.includes('forcepoint')) {
      return 'forcepoint.com';
    }
    if (cleanName.includes('akamai')) {
      return 'akamai.com';
    }
    if (cleanName.includes('f5')) {
      return 'f5.com';
    }
    if (cleanName.includes('barracuda')) {
      return 'barracuda.com';
    }
    if (cleanName.includes('pfsense')) {
      return 'pfsense.org';
    }
    if (cleanName.includes('plixer')) {
      return 'plixer.com';
    }
    if (cleanName.includes('aruba')) {
      return 'arubanetworks.com';
    }
    if (cleanName.includes('airdefense')) {
      return 'extremenetworks.com';
    }
    if (cleanName.includes('aqua security') || cleanName.includes('aquasec')) {
      return 'aquasec.com';
    }
    if (cleanName.includes('quick heal') || cleanName.includes('quickheal')) {
      return 'quickheal.com';
    }
    if (cleanName.includes('kaspersky')) {
      return 'kaspersky.com';
    }
    if (cleanName.includes('cisco')) {
      return 'cisco.com';
    }
    if (cleanName.includes('palo alto') || cleanName.includes('paloalto')) {
      return 'paloaltonetworks.com';
    }
    if (cleanName.includes('sumo logic') || cleanName.includes('sumologic')) {
      return 'sumologic.com';
    }
    if (
      cleanName.includes('fortisoar') ||
      cleanName.includes('fortinet') ||
      cleanName.includes('forti')
    ) {
      return 'fortinet.com';
    }
    if (cleanName.includes('zscaler') || cleanName.includes('zscalar')) {
      return 'zscaler.com';
    }
    if (
      cleanName.includes('digital guardian') ||
      cleanName.includes('digitalguardian')
    ) {
      return 'digitalguardian.com';
    }
    if (
      cleanName.includes('recorded future') ||
      cleanName.includes('recordedfuture')
    ) {
      return 'recordedfuture.com';
    }

    cleanName = cleanName
      .replace(/workspace/g, '')
      .replace(/active directory/g, '')
      .replace(/entra id/g, '')
      .replace(/software/g, '')
      .replace(/platform/g, '')
      .replace(/email configuration/g, '')
      .replace(/llc/g, '')
      .trim();
    const firstWord = cleanName.split(/[\s\-\_]+/)[0];
    if (!firstWord) return '';
    return `${firstWord}.com`;
  }
  onIntegrationSearch(searchValue: string) {
    this.integrationSearchQuery = searchValue;
    this.integrationStateService.setSearchQuery(this.integrationSearchQuery);

    if (this.integrationSearchQuery.trim() === '') {
      this.loadIntegrations();
    } else {
      this.integrationStateService.setSelectedIntegration(null);
      this.loadList('6919b404c2ed5bad29a60d47', {
        platformName: this.integrationSearchQuery,
        sort: '-platformName',
      });
    }
  }

  clearSearch(searchInputField: any) {
    if (searchInputField) {
      searchInputField.value = '';
    }
    this.integrationSearchQuery = '';
    this.integrationStateService.setSearchQuery('');
    this.integrationStateService.setSelectedIntegration(null);
    this.loadIntegrations();
  }

  loadList(formId: any, options: { [key: string]: any } = {}) {
    this.isLoading = true;
    this.cancelApi$.next();
    this.commonService
      .loadListWithOptions(formId, options)
      .pipe(takeUntil(this.cancelApi$))
      .subscribe({
        next: (data) => {
          this.isLoading = false;
          this.responseData = data?.data;

          const records = this.responseData?.records || [];
          const validRecords = records.filter((r: any) => r._id !== 'None');

          const categoriesToFetch = Array.from(
            new Set(
              validRecords
                .map((r: any) => r.category || r.categoryName)
                .filter(Boolean),
            ),
          ) as string[];

          let totalCount = 0;
          this.dynamicIntegrationSections = categoriesToFetch
            .map((cat, index) => {
              const categoryRecords = validRecords.filter((r: any) => {
                const rCat = r.category || r.categoryName;
                return rCat && rCat.toLowerCase() === cat.toLowerCase();
              });

              totalCount += categoryRecords.length;

              let displayLabel = this.formatCategoryLabel(cat);

              return {
                label: `${displayLabel} (${categoryRecords.length.toString().padStart(2, '0')})`,
                originalCategory: cat,
                isExpanded: index === 0,
                items: categoryRecords
                  .map((item: any) => ({
                    moduleId: 'integration-hub',
                    childId: item._id,
                    label: item.platformName,
                    icon:
                      item.iconUrl ||
                      item.icon ||
                      this.integrationIconService.getFallbackIconUrl(item) ||
                      'integration',
                    color: '#ffffff',
                    action: 'external',
                    isConnected: item.status === 'Connected',
                    status: item.status,
                    categoryName: cat,
                  }))
                  .sort((a: any, b: any) => {
                    const aConn = a.status === 'Connected' ? 1 : 0;
                    const bConn = b.status === 'Connected' ? 1 : 0;
                    if (bConn !== aConn) {
                      return bConn - aConn;
                    }
                    return (a.label || '').localeCompare(b.label || '');
                  }),
              };
            })
            .filter((section) => section.items.length > 0);

          this.totalIntegrationsCount = totalCount;
          this.cd.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;
          this.responseData = [];
          this.dynamicIntegrationSections = [];
          this.totalIntegrationsCount = 0;
          this.cd.detectChanges();
        },
      });
  }

  toggleSidebarCollapse(): void {
    // On tablet/iPad screens (< 1024px), fully close the sidebar instead of
    // collapsing to icon-only mode — the user should see only the burger menu.
    if (window.innerWidth < 1024) {
      document.body.classList.remove('sidebar-collapse');
      this.setSidebarWidth(false);
      this.closeSidebar();
      return;
    }

    const isCollapsed = document.body.classList.toggle('sidebar-collapse');
    this.setSidebarWidth(isCollapsed);
    localStorage.setItem('sidebar_collapsed', isCollapsed ? 'true' : 'false');

    // Dispatch window resize events during the 250ms CSS transition to keep charts responsive
    const startTime = Date.now();
    const duration = 300; // slightly longer than CSS transition (250ms)
    const resizeInterval = setInterval(() => {
      window.dispatchEvent(new Event('resize'));
      if (Date.now() - startTime >= duration) {
        clearInterval(resizeInterval);
        // Final resize dispatch to ensure absolute pixel perfect alignment at the end
        window.dispatchEvent(new Event('resize'));
      }
    }, 30);
  }

  private setSidebarWidth(isCollapsed: boolean): void {
    document.body.style.setProperty(
      '--tw-sidebar-width',
      isCollapsed ? this.collapsedSidebarWidth : this.expandedSidebarWidth,
    );
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth < 1024) {
      if (document.body.classList.contains('sidebar-collapse')) {
        document.body.classList.remove('sidebar-collapse');
        this.setSidebarWidth(false);
      }
    } else {
      const isSavedCollapsed =
        localStorage.getItem('sidebar_collapsed') === 'true';
      const isCurrentlyCollapsed =
        document.body.classList.contains('sidebar-collapse');
      if (isSavedCollapsed && !isCurrentlyCollapsed) {
        document.body.classList.add('sidebar-collapse');
        this.setSidebarWidth(true);
      } else if (!isSavedCollapsed && isCurrentlyCollapsed) {
        document.body.classList.remove('sidebar-collapse');
        this.setSidebarWidth(false);
      }
    }
  }

  areAllSectionsCollapsed(): boolean {
    const sections = this.subMenuSections;
    if (!sections || sections.length === 0) return true;
    return sections.every((section) => section.isExpanded === false);
  }

  toggleAllSections(): void {
    const sections = this.subMenuSections;
    if (!sections || sections.length === 0) return;
    const allCollapsed = this.areAllSectionsCollapsed();
    for (const section of sections) {
      section.isExpanded = allCollapsed;
    }
    this.cd.detectChanges();
  }

  // --- Active state checks ---

  isSidebarItemActive(item: SubMenuItemConfig): boolean {
    if (item.moduleId === 'integration-hub') {
      return (
        !!this.selectedIntegration &&
        (item.label?.toLowerCase() === this.selectedIntegration.toLowerCase() ||
          item.childId === this.selectedIntegration)
      );
    }
    if (item.action === 'allTasks') {
      return this.isTasksRouteActive();
    }
    if (item.action === 'myWorkTasks') {
      return this.isMyWorkTasksActive();
    }
    if (item.action === 'myWorkActionables') {
      return (
        this.isRouteActive('my-action-items') ||
        this.isRouteActive('actionables-list') ||
        this.isMyWorkActionableActive()
      );
    }
    if (item.action === 'configuration') {
      return (
        this.router.url.includes('/onboarding') &&
        this.activeStepId === item.stepId
      );
    }
    if (item.childId === 'risk-register' || item.childId === 'system-risk') {
      return this.isSystemRiskActive();
    }
    if (item.childId === 'manual-risk') {
      return this.isManualRiskActive();
    }
    if (item.childId === 'assets-list' || item.route === '/eciso/assets-list') {
      return this.isAssetsRouteActive();
    }
    if (
      item.childId === 'all-action-items' ||
      item.route === '/eciso/all-action-items' ||
      item.route === '/eciso/control-actionables'
    ) {
      return this.isAllActionItemsActive();
    }
    if (item.childId === 'organization') {
      return this.router.url.includes('/organization-setting');
    }
    return item.route ? this.isActive(item.route) : false;
  }

  isTasksRouteActive(): boolean {
    const currentRoute = this.router.url;
    // Exclude my-tasks and pure actionable routes
    if (
      currentRoute.startsWith('/eciso/my-tasks') ||
      currentRoute.startsWith('/eciso/my-action-items') ||
      currentRoute.startsWith('/eciso/actionables-list') ||
      currentRoute.startsWith('/eciso/all-action-items') ||
      currentRoute.startsWith('/eciso/control-actionables') ||
      currentRoute === '/eciso/actionables'
    ) {
      return false;
    }
    // If in task details or detail-related actionables, check navigation source
    if (currentRoute.startsWith('/eciso/tasks/')) {
      try {
        const navCtx = JSON.parse(
          sessionStorage.getItem('taskViewNav') || '{}',
        );
        if (
          navCtx?.source === 'my-tasks' ||
          navCtx?.source === 'actionables' ||
          navCtx?.source === 'all-actionables'
        ) {
          return false;
        }
      } catch {}
      return true;
    }
    return currentRoute.startsWith('/eciso/tasks');
  }

  isSystemRiskActive(): boolean {
    const url = this.router.url.split('?')[0].split('#')[0];
    if (
      url === '/eciso/system-risk' ||
      url === '/eciso/system-risk/' ||
      url.startsWith('/eciso/system-risk/') ||
      url.startsWith('/eciso/risk-register/')
    ) {
      return true;
    }
    if (url.startsWith('/eciso/risk-details')) {
      const lastRiskSource = sessionStorage.getItem('lastRiskSource');
      if (lastRiskSource) {
        return lastRiskSource === 'system-risk';
      }
      let navCtx: any = {};
      try {
        navCtx = JSON.parse(
          sessionStorage.getItem('riskView') ||
            sessionStorage.getItem('manualRiskView') ||
            '{}',
        );
      } catch (e) {}

      return navCtx?.source !== 'manual-risk';
    }
    return false;
  }

  isManualRiskActive(): boolean {
    const url = this.router.url.split('?')[0].split('#')[0];
    if (url.startsWith('/eciso/manual-risk')) {
      return true;
    }
    if (url.startsWith('/eciso/risk-details')) {
      const lastRiskSource = sessionStorage.getItem('lastRiskSource');
      if (lastRiskSource) {
        return lastRiskSource === 'manual-risk';
      }
      let navCtx: any = {};
      try {
        navCtx = JSON.parse(sessionStorage.getItem('manualRiskView') || '{}');
      } catch (e) {}

      return navCtx?.source === 'manual-risk';
    }
    return false;
  }

  isAssetsRouteActive(): boolean {
    const currentRoute = this.router.url.split('?')[0].split('#')[0];
    const normalizedRoute = decodeURIComponent(currentRoute).replace(
      /\/+$/,
      '',
    );
    return (
      normalizedRoute.startsWith('/eciso/assets-list') ||
      normalizedRoute.startsWith('/eciso/asset-details')
    );
  }

  isRouteActive(type: string): boolean {
    const currentRoute = decodeURIComponent(
      this.router.url.split('?')[0].split('#')[0],
    ).replace(/\/+$/, '');

    switch (type) {
      case 'tasks':
        return (
          currentRoute.startsWith('/eciso/tasks') &&
          !currentRoute.includes('/actionables/')
        );
      case 'my-action-items':
      case 'actionables-list':
        return (
          currentRoute.startsWith('/eciso/my-action-items') ||
          currentRoute.startsWith('/eciso/actionables-list')
        );
      default:
        return false;
    }
  }

  isMyWorkTasksActive(): boolean {
    const currentRoute = this.router.url;
    if (currentRoute.startsWith('/eciso/my-tasks')) {
      return true;
    }
    // If in task details or detail-related actionables, check navigation source
    if (currentRoute.startsWith('/eciso/tasks/')) {
      try {
        const navCtx = JSON.parse(
          sessionStorage.getItem('taskViewNav') || '{}',
        );
        if (navCtx?.source === 'my-tasks') {
          return true;
        }
      } catch {}
    }
    return false;
  }

  isMyWorkActionableActive(): boolean {
    const url = this.router.url;
    // If we are within a task's details page (including nested actionables)
    if (url.startsWith('/eciso/tasks/')) {
      try {
        const navCtx = JSON.parse(
          sessionStorage.getItem('taskViewNav') || '{}',
        );
        if (navCtx?.source === 'actionables') {
          return true;
        }
      } catch {}
      return false;
    }
    return (
      url.startsWith('/eciso/my-action-items') ||
      url.startsWith('/eciso/actionables-list') ||
      url.includes('/actionables')
    );
  }

  isAllActionItemsActive(): boolean {
    const url = this.router.url;
    // If we are within a task's details page (including nested actionables)
    if (url.startsWith('/eciso/tasks/')) {
      try {
        const navCtx = JSON.parse(
          sessionStorage.getItem('taskViewNav') || '{}',
        );
        if (navCtx?.source === 'all-actionables') {
          return true;
        }
      } catch {}
      return false;
    }
    return (
      url.startsWith('/eciso/all-action-items') ||
      url.startsWith('/eciso/control-actionables')
    );
  }

  isActive(route: string): boolean {
    const currentRoute = this.router.url.split('?')[0].split('#')[0];
    const normalizedRoute = decodeURIComponent(currentRoute).replace(
      /\/+$/,
      '',
    );

    if (
      route === '/eciso/framework-library' &&
      normalizedRoute.startsWith('/eciso/framework-details')
    ) {
      return true;
    }

    if (
      route === '/eciso/assets-list' &&
      (normalizedRoute.startsWith('/eciso/asset-details') ||
        normalizedRoute.startsWith('/eciso/assets-list'))
    ) {
      return true;
    }

    return normalizedRoute.startsWith(route);
  }

  // --- Click / Navigation Logic ---

  onSidebarSectionClick(section: any): void {
    if (this.currentModuleName === 'Integrations Hub') {
      const isSearchActive = !!(
        this.integrationSearchQuery && this.integrationSearchQuery.trim() !== ''
      );

      if (isSearchActive) {
        section.isExpanded = true;
      } else {
        const willExpand = section.isExpanded === false;
        if (willExpand) {
          const sections = this.subMenuSections;
          for (const sec of sections) {
            if (sec !== section) {
              sec.isExpanded = false;
            }
          }
        }
        section.isExpanded = willExpand;
      }

      if (section.originalCategory) {
        this.integrationStateService.setSelectedCategory(
          section.originalCategory,
        );
        this.integrationStateService.setSelectedIntegration(null);
      }
      this.cd.detectChanges();
    }
  }

  onSidebarItemClick(item: SubMenuItemConfig, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    switch (item.action) {
      case 'myWorkTasks':
        this.goToMyWorkTasks();
        return;
      case 'myWorkActionables':
        this.onMyWorkActionablesClick();
        return;
      case 'allTasks':
        this.onTasksMenuClick();
        return;
      case 'configuration':
        if (item.stepId) {
          this.loadForm(item.stepId);
        }
        return;
      case 'external':
        if (item.moduleId === 'integration-hub') {
          const catName = (item as any).categoryName;

          if (catName) {
            this.integrationStateService.setSelectedCategory(catName);
          }
          this.integrationStateService.setSelectedIntegration(
            item.label || item.childId || null,
          );
          this.cd.detectChanges();
        } else if (item.externalUrl) {
          window.open(item.externalUrl, '_blank');
        }
        return;
      default:
        if (item.route) {
          this.navigateTo(item.route);
        }
    }
  }

  goToMyWorkTasks(): void {
    const email = this.profileResponse?.email || '';
    const STORAGE_KEY = 'myTaskView';
    const existing = sessionStorage.getItem(STORAGE_KEY);
    const prevState = existing ? JSON.parse(existing) : {};
    delete prevState.assigned;
    delete prevState.assignedUser;

    if (email) {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...prevState,
          taskAssigned: email,
        }),
      );
      this.router.navigate(['/eciso/my-tasks']);
    } else {
      // Fallback: fetch profile then navigate
      this.authService
        .getProfile(localStorage.getItem('accessToken'))
        .subscribe({
          next: (data) => {
            const userEmail = data?.data?.email || '';
            sessionStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({
                ...prevState,
                taskAssigned: userEmail,
              }),
            );
            this.router.navigate(['/eciso/my-tasks']);
          },
          error: () => {
            this.router.navigate(['/eciso/my-tasks']);
          },
        });
    }
  }

  onTasksMenuClick(): void {
    const STORAGE_KEY = 'tasksView';
    const storedView = sessionStorage.getItem(STORAGE_KEY);
    const prevState = storedView ? JSON.parse(storedView) : {};

    delete prevState.taskAssigned;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(prevState));
    sessionStorage.removeItem('fromEvidence');

    setTimeout(() => {
      this.router.navigate(['/eciso/tasks']);
    }, 0);
  }

  onMyWorkActionablesClick(): void {
    const STORAGE_KEY = 'actionableView';
    const existing = sessionStorage.getItem(STORAGE_KEY);
    const prevState = existing ? JSON.parse(existing) : {};

    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...prevState,
        assigned: this.profileResponse?.email || null,
        currentPage: 1,
      }),
    );

    this.router.navigate(['/eciso/my-action-items']);
  }

  navigateTo(path: string) {
    if (!['/tasks', '/actionables'].some((p) => path.includes(p))) {
      this.clearWorkFilters();
    }

    this.closeSidebar();

    setTimeout(() => {
      this.router.navigateByUrl(path);
    }, 0);
  }

  clearWorkFilters() {
    try {
      const tasksView = JSON.parse(sessionStorage.getItem('tasksView') || '{}');
      if (tasksView?.taskAssigned) {
        delete tasksView.taskAssigned;
        sessionStorage.setItem('tasksView', JSON.stringify(tasksView));
      }
    } catch {}

    try {
      const actionableView = JSON.parse(
        sessionStorage.getItem('actionableView') || '{}',
      );
      if (actionableView?.assigned) {
        delete actionableView.assigned;
        sessionStorage.setItem(
          'actionableView',
          JSON.stringify(actionableView),
        );
      }
    } catch {}
  }

  closeSidebar() {
    const drawerEl = document.querySelector('#sidebar');
    if (drawerEl) {
      drawerEl.classList.remove('active');
      if ((window as any).KTDrawer) {
        const drawer = (window as any).KTDrawer.getInstance(drawerEl);
        if (drawer && drawer.isShown()) {
          drawer.hide();
        }
      }
    }
  }

  loadForm(formId: any) {
    this.formService.loadFormData(formId).subscribe({
      next: (data) => {
        let formRecordID = data?.data?.formCollection?.recordId;
        if (formRecordID) {
          const currentUrl = this.router.url;
          const targetUrl = `/eciso/onboarding/${formId}/${formRecordID}`;

          if (!currentUrl.startsWith(targetUrl)) {
            this.router.navigate(['/eciso/onboarding', formId, formRecordID]);
          }
        }
      },
      error: (err) => console.error('Error loading form JSON:', err),
    });
  }

  onItemMouseEnter(event: MouseEvent, item: SubMenuItemConfig): void {
    if (!this.isSidebarCollapsed) return;
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    this.hoveredItem = item;
    this.tooltipTop = rect.top + rect.height / 2;
    this.cd.detectChanges();
  }

  onItemMouseLeave(): void {
    this.hoveredItem = null;
    this.cd.detectChanges();
  }

  resetScrollbarTimer(): void {
    this.isMouseOverSidebar = true;
    if (this.isSmallerDesktop) {
      if (this.scrollbarTimeout) {
        clearTimeout(this.scrollbarTimeout);
        this.scrollbarTimeout = undefined;
      }
      this.hideScrollbar = true;
      this.cd.detectChanges();

      this.scrollbarTimeout = setTimeout(() => {
        if (this.isMouseOverSidebar) {
          this.hideScrollbar = false;
          this.cd.detectChanges();
        }
        this.scrollbarTimeout = undefined;
      }, 2000);
    } else {
      // Only start a fresh timer if one isn't already running
      if (this.scrollbarTimeout) return;
      this.scrollbarTimeout = setTimeout(() => {
        this.hideScrollbar = true;
        this.scrollbarTimeout = undefined;
        this.cd.detectChanges();
      }, 3000);
    }
  }

  /** Briefly show scrollbar during scroll, then re-hide after 1.5s */
  showScrollbarBriefly(): void {
    if (this.isSmallerDesktop) {
      if (this.hideScrollbar) {
        this.hideScrollbar = false;
        this.cd.detectChanges();
      }
      if (this.scrollbarTimeout) {
        clearTimeout(this.scrollbarTimeout);
        this.scrollbarTimeout = undefined;
      }
    } else {
      if (this.hideScrollbar) {
        this.hideScrollbar = false;
        this.cd.detectChanges();
      }
      if (this.scrollbarTimeout) {
        clearTimeout(this.scrollbarTimeout);
      }
      this.scrollbarTimeout = setTimeout(() => {
        this.hideScrollbar = true;
        this.scrollbarTimeout = undefined;
        this.cd.detectChanges();
      }, 1500);
    }
  }

  clearScrollbarTimer(): void {
    this.isMouseOverSidebar = false;
    if (this.scrollbarTimeout) {
      clearTimeout(this.scrollbarTimeout);
      this.scrollbarTimeout = undefined;
    }
    if (this.isSmallerDesktop) {
      this.hideScrollbar = true;
      this.cd.detectChanges();
    } else {
      if (this.hideScrollbar) {
        this.hideScrollbar = false;
        this.cd.detectChanges();
      }
    }
  }
}
